import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';

import type { RoleUserRecord } from '../_graphql/useRoleUsersQuery';
import { useUserRoleRevokeMutation } from '../_graphql/useUserRoleRevokeMutation';

import { $selectedRoleUserIds } from '~/modules/settings/_state/roleForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

export const useRoleUsersBulkActions = (allUsers: RoleUserRecord[]) => {
  const t = useTranslations();
  const { toast } = useToast();
  const revokeMutation = useUserRoleRevokeMutation();

  const [selectedUserIds, setSelectedUserIds] = useAtom($selectedRoleUserIds);
  const [isBulkActionRunning, setIsBulkActionRunning] = React.useState(false);
  const [pendingBulkRemove, setPendingBulkRemove] = React.useState(false);
  const [userToRemove, setUserToRemove] = React.useState<RoleUserRecord | null>(
    null,
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedUserIds),
    [selectedUserIds],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedUserIds(Array.from(keys) as string[]);
    },
    [setSelectedUserIds],
  );

  const inviteSentUserIds = React.useMemo(
    () =>
      allUsers
        .filter(
          (u) => selectedUserIds.includes(u.id) && u.status === 'invite_sent',
        )
        .map((u) => u.id),
    [allUsers, selectedUserIds],
  );

  const handleResendInvite = (user: RoleUserRecord) => {
    console.log('Resend invite to user:', user.id);
  };

  const handleRemoveFromRole = (user: RoleUserRecord) => {
    setUserToRemove(user);
  };

  const handleRemoveConfirm = async () => {
    if (!userToRemove) return;
    try {
      await revokeMutation.mutateAsync({
        assignmentId: userToRemove.assignmentId,
      });
      setUserToRemove(null);
      toast({
        title:
          t.teamAndPermissions.roles.details.users.removeFromRoleDialog.success,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title:
          t.teamAndPermissions.roles.details.users.removeFromRoleDialog.error,
        description: err instanceof Error ? err.message : undefined,
        variant: 'error',
      });
    }
  };

  const handleRemoveDialogOpenChange = (open: boolean) => {
    if (!revokeMutation.isPending) {
      setUserToRemove(open ? userToRemove : null);
    }
  };

  const handleBulkRemoveFromRole = () => {
    if (selectedUserIds.length === 0) return;
    setPendingBulkRemove(true);
  };

  const handleConfirmBulkRemove = async () => {
    setIsBulkActionRunning(true);
    try {
      const usersToRemove = allUsers.filter((u) =>
        selectedUserIds.includes(u.id),
      );
      await Promise.all(
        usersToRemove.map((u) =>
          revokeMutation.mutateAsync({ assignmentId: u.assignmentId }),
        ),
      );
      toast({
        title: t.teamAndPermissions.roles.details.users.bulk.removeSuccess(
          usersToRemove.length,
        ),
        variant: 'success',
      });
      setSelectedUserIds([]);
      setPendingBulkRemove(false);
    } catch (err) {
      toast({
        title: t.teamAndPermissions.roles.details.users.bulk.removeError,
        description: err instanceof Error ? err.message : undefined,
        variant: 'error',
      });
    } finally {
      setIsBulkActionRunning(false);
    }
  };

  const handleBulkRemoveDialogOpenChange = (open: boolean) => {
    if (!isBulkActionRunning) {
      setPendingBulkRemove(open);
    }
  };

  const handleBulkResendInvite = () => {
    allUsers
      .filter((u) => inviteSentUserIds.includes(u.id))
      .forEach((u) => handleResendInvite(u));
  };

  const handleClearSelection = () => {
    setSelectedUserIds([]);
  };

  return {
    selectedUserIds,
    selectedKeys,
    handleSelectionChange,
    handleClearSelection,
    isBulkActionRunning,
    inviteSentUserIds,
    handleResendInvite,
    handleRemoveFromRole,
    userToRemove,
    handleRemoveConfirm,
    handleRemoveDialogOpenChange,
    isRemovePending: revokeMutation.isPending,
    pendingBulkRemove,
    handleBulkRemoveFromRole,
    handleConfirmBulkRemove,
    handleBulkRemoveDialogOpenChange,
    handleBulkResendInvite,
  };
};
